import { useState, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, IconButton } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import LandingImg1 from '../../assets/images/landing1.jpg';
import LandingImg2 from '../../assets/images/landing2.jpg';
import LandingImg3 from '../../assets/images/landing3.jpg';
import LandingImg4 from '../../assets/images/landing4.jpg';
import LandingMenu from './components/LandingMenu';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import LandingCover from './components/LandingCover';
import { DEFAULT_NOTE_GALLERY_PAGE } from '../../utils/constant';
import MbLandingbg from '../../assets/images/mb-landingbg.jpg';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';

const PageContainer = styled(Box)(({ theme }) => ({
  width: '100vw',
  height: '100vh',
  background: 'linear-gradient(327.1deg, #E3D0AD 0.64%, #EBE1D5 100%)',
  display: 'flex',
  alignItems: 'center',
  '& >section': {
    width: '50%',
    position: 'relative',

    [theme.breakpoints.down('md')]: {
      width: '100%',
      height: '100vh',
    },
  },
  [theme.breakpoints.down('md')]: {
    height: 'auto',
    flexDirection: 'column',
  },
}));

const LeftSec = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  paddingLeft: '7vw',

  [theme.breakpoints.down('md')]: {
    backgroundImage: `url(${MbLandingbg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '0 17px',
  },
}));

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  '&>*': {
    width: '100%',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    margin: '0 auto',
  },
}));

const RightSec = styled('section')({
  '&>*': {
    height: '100vh',
  },
});

const Title = styled('h1')(({ theme }) => ({
  fontFamily: 'Raleway',
  fontWeight: 700,
  fontSize: '40px',
  lineHeight: '56px',
  margin: '0',
  [theme.breakpoints.down('md')]: {
    color: '#fff',
    textAlign: 'center',
    fontSize: '36px',
  },
}));

const SubTitle = styled('p')(({ theme }) => ({
  fontFamily: 'Caveat',
  fontWeight: 700,
  fontSize: '24px',
  lineHeight: '38px',
  color: '#717171',
  position: 'relative',
  paddingLeft: '54px',
  margin: '11px 0 89px',
  [theme.breakpoints.down('md')]: {
    color: 'rgba(255, 255, 255, 0.7)',
    textAlign: 'center',
    margin: '17px auto 90px',
    width: 'fit-content',
    fontSize: '22px',
    paddingLeft: '34px',
  },
  '&:before': {
    content: '""',
    position: 'absolute',
    left: '0',
    top: '19px',
    width: '40px',
    border: '1px solid',
    [theme.breakpoints.down('md')]: {
      width: '20px',
    },
  },
}));

const Gobutton = styled(Button)(({ theme }) => ({
  fontFamily: 'Fjalla One',
  fontWeight: 400,
  fontSize: '24px',
  lineHeight: '45px',
  display: 'flex',
  alignItems: 'center',
  border: '2px solid #402B23',
  height: '49px',
  maxWidth: 'fit-content',
  padding: '0 26px',
  color: '#402B23',
  borderRadius: '0',
  margin: '0',
  '& svg': {
    marginLeft: '7px',
    width: '20px',
    height: 'auto',
  },
  [theme.breakpoints.down('md')]: {
    textAlign: 'center',
    margin: '0 auto',
    color: '#fff',
    borderColor: '#AA9178',
  },
}));

const CoverContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  height: '100vh',
  '&>*:not(button)': {
    width: '50%',
    position: 'absolute',
    left: '0',
    top: '0',
  },
  '&>img:first-of-type': {
    height: '55vh',
  },
  '&>img:nth-of-type(2)': {
    height: '40vh',
    left: 'auto',
    top: '0',
    right: '0',
  },

  '&>img:nth-of-type(3)': {
    height: '60vh',
    left: 'auto',
    right: '0',
    top: 'auto',
    bottom: '0',
  },
  '&>div': {
    height: '45vh',
    top: 'auto',
    bottom: '0',
  },
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    flexDirection: 'column',
    '&>img:first-of-type': {
      height: '37vh',
    },
    '&>img:nth-of-type(2)': {
      height: '22vh',
    },
    '&>img:nth-of-type(3)': {
      height: '33vh',
      bottom: '45vh',
    },
    '&>img:last-of-type': {
      height: '18vh',
      bottom: '45vh',
      top: 'auto',
    },
    '&>div': {
      height: '45vh',
      width: '100%',
    },
  },
}));

const LandingImage = styled('img')({
  objectFit: 'cover',
});

const ScrollButton = styled(IconButton)({
  position: 'absolute',
  bottom: '10px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 'auto',
  '& svg': {
    height: '30px',
    width: '30px',
    color: 'rgba(255, 255, 255, 0.54)',
  },
  '&:hover svg': {
    color: '#fff',
  },
});

const LandingPage = () => {
  const [cover, setCover] = useState(false);
  const bottomRef = useRef(null);
  const topRef = useRef(null);

  const mobileMatch = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const switchCover = (content) => {
    setCover(content);
  };

  const closeCover = () => {
    setCover(false);
  };

  const scrollClick = (direct) => {
    if (direct === 'goBottom') {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <PageContainer>
      <LeftSec ref={topRef}>
        <Container>
          <Title>The Realm of Coffee</Title>
          <SubTitle>find, share, like everything about coffee</SubTitle>
          <Gobutton href={DEFAULT_NOTE_GALLERY_PAGE}>
            Let’s Go <ArrowRightAltIcon />
          </Gobutton>
          {mobileMatch && (
            <ScrollButton
              onClick={() => {
                scrollClick('goBottom');
              }}
            >
              <ExpandMoreRoundedIcon />
            </ScrollButton>
          )}
        </Container>
      </LeftSec>
      <RightSec ref={bottomRef}>
        <CoverContainer>
          <LandingImage src={LandingImg1} />
          <LandingImage src={LandingImg2} />
          <LandingImage src={LandingImg3} />
          {mobileMatch && <LandingImage src={LandingImg4} />}
          <LandingMenu switchCover={switchCover} />
          {mobileMatch && (
            <ScrollButton
              onClick={() => {
                scrollClick('goTop');
              }}
            >
              <ExpandLessRoundedIcon />
            </ScrollButton>
          )}
        </CoverContainer>
        <LandingCover closeCover={closeCover} cover={cover} />
      </RightSec>
    </PageContainer>
  );
};

export default LandingPage;
